import { Box, Button, Chip, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import moment from "moment";
import { ReimbursementRequest, CaseWhenBenefit } from "services/GraphQl";
import FormattedCurrency from "components/WhenBenefit/FormattedCurrency";

interface ReimbursementRequestTableProps {
  caseWhenBenefit: CaseWhenBenefit;
  onViewReimbursement: (reimbursement: ReimbursementRequest) => void;
}

const getStatusColor = (status: string) => {
  const normalizedStatus = status?.toLowerCase();
  switch (normalizedStatus) {
    case "approved":
    case "partially approved":
      return "success";
    case "pending":
    case "additional information requested":
      return "warning";
    case "denied":
    case "cancelled":
      return "error";
    default:
      return "default";
  }
};

const reimbursementRequestColumns = [
  {
    field: "createdAt",
    headerName: "Date Requested",
    flex: 1,
    minWidth: 120,
    align: "center" as const,
    headerAlign: "center" as const,
    valueFormatter: (params: any) => {
      return moment(params?.value).format("MM/DD/YYYY");
    },
  },
  {
    field: "status",
    headerName: "Status",
    flex: 1,
    minWidth: 150,
    align: "center" as const,
    headerAlign: "center" as const,
    renderCell: (params: any) => {
      return (
        <Chip
          label={params.value}
          color={getStatusColor(params.value) as any}
          size="small"
          variant="filled"
        />
      );
    },
  },
  {
    field: "amount",
    headerName: "Amount requested",
    flex: 1,
    minWidth: 160,
    align: "center" as const,
    headerAlign: "center" as const,
    renderCell: (params: any) => {
      return <FormattedCurrency value={params.value} />;
    },
  },
  {
    field: "approvedAmount",
    headerName: "Amount approved",
    flex: 1,
    minWidth: 160,
    align: "center" as const,
    headerAlign: "center" as const,
    renderCell: (params: any) => {
      return <FormattedCurrency value={params.value} />;
    },
  },
  {
    field: "coverageMonths",
    headerName: "Coverage Months",
    flex: 1.5,
    minWidth: 200,
    align: "center" as const,
    headerAlign: "center" as const,
    valueGetter: (params: any) => {
      return (params.value || [])
        .map((m: string) => moment(m, "YYYY-MM").format("MMMM YYYY"))
        .join(", ");
    },
  },
  {
    field: "actions",
    headerName: "Actions",
    sortable: false,
    flex: 1,
    minWidth: 120,
    align: "center" as const,
    headerAlign: "center" as const,
    disableClickEventBubbling: true,
    renderCell: (params: any) => {
      return (
        <Button
          variant="contained"
          color="primary"
          size="small"
          sx={{
            backgroundColor: "#2A876C",
            "&:hover": {
              backgroundColor: "#1f6450",
            },
          }}
          onClick={() => {
            params.row.onViewReimbursement(params.row);
          }}
        >
          View
        </Button>
      );
    },
  },
];

function ReimbursementRequestTable({ caseWhenBenefit, onViewReimbursement }: ReimbursementRequestTableProps) {
  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
        Request History
      </Typography>
      <Box
        sx={{
          backgroundColor: "white",
          border: "1px solid #e0e0e0",
          borderRadius: 1,
        }}
      >
        <DataGrid
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25, page: 0 },
            },
          }}
          sx={{
            border: "none",
            backgroundColor: "white",
            "& .MuiDataGrid-main": {
              backgroundColor: "white",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: "white",
            },
            "& .MuiDataGrid-virtualScrollerContent": {
              backgroundColor: "white",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "white",
              borderBottom: "1px solid #e0e0e0",
              color: "#666",
              fontWeight: 600,
              fontSize: "0.875rem",
            },
            "& .MuiDataGrid-columnHeader": {
              backgroundColor: "white",
              "&:focus": {
                outline: "none",
              },
              "&:focus-within": {
                outline: "none",
              },
            },
            "& .MuiDataGrid-row": {
              borderBottom: "1px solid #f0f0f0",
              backgroundColor: "white !important",
              "&:hover": {
                backgroundColor: "#f9f9f9",
              },
              "&.Mui-even": {
                backgroundColor: "white !important",
              },
              "&.Mui-odd": {
                backgroundColor: "white !important",
              },
            },
            "& .MuiDataGrid-cell": {
              borderRight: "none",
              padding: "16px",
              backgroundColor: "white",
              "&:focus": {
                outline: "none",
              },
              "&:focus-within": {
                outline: "none",
              },
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "1px solid #e0e0e0",
              backgroundColor: "white",
            },
            "& .MuiTablePagination-root": {
              "& .MuiTablePagination-selectLabel": {
                fontSize: "0.875rem",
              },
              "& .MuiTablePagination-input": {
                fontSize: "0.875rem",
              },
              "& .MuiTablePagination-displayedRows": {
                fontSize: "0.875rem",
              },
            },
          }}
          rows={caseWhenBenefit?.reimbursementRequests?.map((req, idx) => ({
            ...req,
            id: req.reimbursementRequestId || idx,
            onViewReimbursement,
          })) || []}
          columns={reimbursementRequestColumns}
          autoHeight
          disableRowSelectionOnClick
          hideFooterSelectedRowCount
        />
      </Box>
    </Box>
  );
}

export default ReimbursementRequestTable; 